import { Download, Camera, FileText, Monitor, Award, Mail } from "lucide-react";
import { CopyEmail } from "./CopyEmail";

const PRESS_PHOTOS = [
  {
    url: "/images/press-photo-5.png",
    label: "PRESS PHOTO 1",
    objectPosition: "center top",
  },
  {
    url: "/images/press-photo-6.png",
    label: "PRESS PHOTO 2",
    objectPosition: "center top",
  },
  {
    url: "/images/press-photo-7.png",
    label: "PRESS PHOTO 3",
    objectPosition: "center top",
  },
  {
    url: "/images/press-photo-9.png",
    label: "PRESS PHOTO 4",
  },
  {
    url: "/images/press-photo-10.png",
    label: "PRESS PHOTO 5",
  },
  {
    url: "/images/show-coda.jpg",
    label: "PRESS PHOTO 6",
  },
  {
    url: "/images/show-liveset.jpg",
    label: "PRESS PHOTO 7",
  },
  {
    url: "/images/show-behind-decks.jpg",
    label: "PRESS PHOTO 8",
  },
];

export const EPKSection = () => {
  const handleDownloadPhoto = async (url, label) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const ext = url.split(".").pop() || "jpg";
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `alchemist-ca-${label.toLowerCase().replace(/\s/g, "-")}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke after a tick to let the browser start the download
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      console.error("Failed to download photo:", err);
      // Fallback: open the image in a new tab so the user can save it manually
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section id="epk" data-testid="epk-section" className="py-24 md:py-32 tracing-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="mb-16">
          <p className="font-sub text-[10px] text-white/30 tracking-[0.3em] mb-4">004</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter">PRESS KIT</h2>
          <div className="gold-line mt-6" />
          <p className="font-body text-white/40 text-sm mt-6 max-w-xl">
            Everything you need to promote Alchemist (CA) at your event.
            Download press photos, review the tech rider, and check achievements.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Bio Card */}
          <div className="border border-white/10 p-8 hover:border-white/20 transition-colors duration-300">
            <FileText className="w-5 h-5 text-white/30 mb-6" />
            <h3 className="font-heading text-lg tracking-tight mb-4">BIO</h3>
            <div className="font-body text-white/40 text-sm leading-relaxed space-y-4">
              <p>
                Alchemist is a producer and DJ based in Toronto, drawing from influences across
                Deep House, Melodic House and Techno. Blending modern electronic production with
                Indian instruments and melodic elements, his sound focuses on atmosphere, emotion,
                and groove.
              </p>
              <p>
                He has earned support within the electronic community and placements on several
                charts, including a Top-2 Trance Hype release and a Top-12 Melodic House & Techno
                Hype position on Beatport.
              </p>
              <p>
                As a DJ, Alchemist has had the opportunity to play both opening and closing sets
                alongside artists like Eelke Kleijn, Fideles, Rebuke, Cassian, Stephan Bodzin,
                Simon Doty, Amy Wiles, Enamour and many more at venues including History, Coda,
                Story and select warehouse events in Toronto.
              </p>
            </div>
          </div>

          {/* Achievements Card */}
          <div className="border border-white/10 p-8 hover:border-white/20 transition-colors duration-300">
            <Award className="w-5 h-5 text-white/30 mb-6" />
            <h3 className="font-heading text-lg tracking-tight mb-4">ACHIEVEMENTS</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="font-heading text-xl text-white mt-0.5">TOP 2</span>
                <div>
                  <p className="font-body text-white/80 text-sm">Trance Hype Release</p>
                  <p className="font-sub text-[10px] text-white/30 tracking-widest">BEATPORT CHARTS</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-heading text-xl text-white mt-0.5">TOP 12</span>
                <div>
                  <p className="font-body text-white/80 text-sm">Melodic House & Techno Hype</p>
                  <p className="font-sub text-[10px] text-white/30 tracking-widest">BEATPORT CHARTS</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="font-heading text-lg text-white mt-0.5">6+</span>
                <div>
                  <p className="font-body text-white/80 text-sm">Notable Support Sets</p>
                  <p className="font-sub text-[10px] text-white/30 tracking-widest">FIDELES, REBUKE, CASSIAN & MORE</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Tech Rider Card */}
          <div className="border border-white/10 p-8 hover:border-white/20 transition-colors duration-300">
            <Monitor className="w-5 h-5 text-white/30 mb-6" />
            <h3 className="font-heading text-lg tracking-tight mb-4">TECH RIDER</h3>
            <div className="space-y-4">
              <div>
                <p className="font-sub text-[10px] text-white/30 tracking-widest mb-2">DJ SETUP</p>
                <ul className="font-body text-white/50 text-sm space-y-1.5">
                  <li>2x CDJ-3000 / SC6000 (or equivalent)</li>
                  <li>1x DJM-900NXS2 / DJM-A9 (or equivalent)</li>
                  <li>RMX-1000 (optional)</li>
                </ul>
              </div>
              <div>
                <p className="font-sub text-[10px] text-white/30 tracking-widest mb-2">MONITORING</p>
                <ul className="font-body text-white/50 text-sm space-y-1.5">
                  <li>DJ booth monitor (minimum 1x)</li>
                  <li>Headphone cue system</li>
                </ul>
              </div>
              <div>
                <p className="font-sub text-[10px] text-white/30 tracking-widest mb-2">STAGE</p>
                <ul className="font-body text-white/50 text-sm space-y-1.5">
                  <li>Elevated DJ booth preferred</li>
                  <li>Adequate lighting for DJ area</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Press Photos */}
        <div className="mt-16">
          <div className="flex items-center justify-between mb-8">
            <p className="font-sub text-[10px] text-white/40 tracking-[0.3em] flex items-center gap-2">
              <Camera className="w-3 h-3" />
              PRESS PHOTOS
            </p>
            <p className="font-sub text-[10px] text-white/20 tracking-widest">CLICK TO DOWNLOAD</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PRESS_PHOTOS.map((photo, i) => (
              <button
                key={photo.url}
                data-testid={`epk-photo-${i}`}
                onClick={() => handleDownloadPhoto(photo.url, photo.label)}
                className="group relative aspect-square overflow-hidden border border-white/5 hover:border-white/20 transition-colors duration-300"
              >
                <img
                  src={photo.url}
                  alt={photo.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{ objectPosition: photo.objectPosition || "center" }}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#050505]/40 group-hover:bg-[#050505]/20 transition-colors duration-300" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <p className="font-sub text-[9px] text-white/50 tracking-widest">{photo.label}</p>
                  <Download className="w-3 h-3 text-white/30 group-hover:text-white/70 transition-colors duration-300" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Booking Contact */}
        <div className="mt-8 border border-white/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="font-sub text-[10px] text-white/40 tracking-[0.3em]">BOOKINGS & PRESS INQUIRIES</p>
          <div className="flex items-center gap-3">
            <a
              data-testid="epk-contact-email"
              href="mailto:contact@dj-alchemist.com"
              title="Send email"
              className="text-white/40 hover:text-white transition-colors duration-300"
            >
              <Mail className="w-3.5 h-3.5" />
            </a>
            <CopyEmail testId="epk-copy-email" className="text-xs text-white/60 hover:text-white" />
          </div>
        </div>
      </div>
    </section>
  );
};
